import { Box, Flex, Text } from '@chakra-ui/react';
import { Button, TextArea, useToast } from '@morressier/alembic';
import { useRequestImmediateRevision } from 'src/api/commands';
import { IDecisionModalProps } from './decisionModalHelper';

import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { prepareResponseSchema } from '@publishing-engine/domain/validation';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { Overlay } from '../../Overlay';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import ComposeDecisionEmail, { EmailContentForm } from './ComposeDecisionEmail';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { useSteps } from './hooks';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import DecisionLayout from './DecisionLayout';
import { useEffect } from 'react';
import ResponseToAuthorField from './ResponseToAuthorField';

export const RequestImmediateRevisionModal = createEditorialModal(
  ({
    onClose,
    submissionId,
    submissionContextId,
    organizationId
  }: IDecisionModalProps) => {
    const toast = useToast();

    const {
      refetchSubmission,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'EditorialRequestImmediateRevision'
    });

    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const methods = useForm({
      defaultValues: {
        editorComment: ''
      },
      resolver: zodResolver(prepareResponseSchema)
    });

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);
    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const requestRevisionMutation = useRequestImmediateRevision(
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent)
    );

    const requestRevision = async ({
      editorComment
    }: {
      editorComment: string;
    }) => {
      const res = await requestRevisionMutation.mutateAsync(editorComment);

      if (res.isSuccess) {
        await submitEditorNote(editorNote);
        toast({
          variant: 'success',
          title: 'Submission In Revision',
          description: 'The author has been invited to revise this Submission.'
        });
        onClose();
      } else {
        toast({
          variant: 'error',
          title: 'Error requesting revision',
          description:
            'There was an error requesting revision on the submission.'
        });
      }
    };

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        methods.handleSubmit(requestRevision)();
      }
    };

    const onSubmitEmailStep = () => {
      methods.handleSubmit(requestRevision)();
    };

    const renderFirstStep = () => (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitFirstStep)}>
          <Box marginX="auto">
            <StepHeader
              currentStep={1}
              totalSteps={hasCustomizableTemplates ? 2 : 0}
              heading="Request Immediate Revision"
              description="Proceeding with this decision confirms you have reviewed the
                    submission. The author will be informed of this decision."
              marginY={2}
            />
          </Box>
          <Box paddingBottom={8} overflowY="auto">
            <Flex flexDirection="column" gap={6}>
              <Flex gap={2} flexDirection="column">
                <ResponseToAuthorField
                  submissionId={submissionId}
                  decisionType="EditorialRequestImmediateRevision"
                />
                <Flex alignItems="center">
                  <Text textStyle="headingItem">Response to Author</Text>
                  <Box
                    as="span"
                    color="actionSecondaryCautionContent"
                    textStyle="headingItem"
                  >
                    *
                  </Box>
                </Flex>
                <TextArea
                  value={methods.watch('editorComment')}
                  {...methods.register('editorComment')}
                  placeholder="Add feedback to the author"
                  isInvalid={Boolean(methods.formState.errors.editorComment)}
                  errorMessage={
                    methods.formState.errors?.editorComment?.message
                  }
                  rows={4}
                  maxLength={10000}
                />
              </Flex>
              <EditorialNote value={editorNote} onChange={setEditorNote} />
            </Flex>
          </Box>
        </form>
      </FormProvider>
    );

    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form onSubmit={emailContentMethods.handleSubmit(onSubmitEmailStep)}>
          <Box marginX="auto">
            <StepHeader
              currentStep={2}
              totalSteps={2}
              heading="Send Email Notification"
              description="Compose an email to notify the author about the immediate revision request."
              marginY={2}
            />
          </Box>
          <ComposeDecisionEmail
            organizationId={organizationId}
            submissionContextId={submissionContextId}
            commandName="EditorialRequestImmediateRevision"
            formPayload={methods.getValues()}
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(requestRevision)()}
                isLoading={
                  !hasCustomizableTemplates && requestRevisionMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() =>
              emailContentMethods.handleSubmit(onSubmitEmailStep)()
            }
            isLoading={requestRevisionMutation.isPending}
          >
            Send Request
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout manuscriptId={manuscriptId} onClose={onClose}>
          {currentStep === 1
            ? renderFirstStep()
            : hasCustomizableTemplates
            ? renderEmailStep()
            : null}
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
